// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/** The transfer-assessment risk bands, lowest first (the 5 × 5 bands of OR-13). */
export const TRANSFER_RISK_LEVELS = ['low', 'medium', 'high', 'critical'];
// ── Sprint 62.20.0 — risk-tiered transfer screening inputs (AC3) ──
/**
 * The `event_type` a pack puts on a `grc_regulatory_deadlines` record to
 * declare which screening INPUTS a tiered transfer control would read.
 *
 * Platform selector vocabulary (the 64.153.0 tradition). `request_types[]` are
 * the input codes and `request_type_labels[code]` their bilingual names, so the
 * screen renders the pack's own words. Nothing in `src/` names an instrument,
 * an authority or an input.
 */
export const TRANSFER_SCREENING_INPUT_EVENT_TYPE = 'transfer_screening_input';
/**
 * The `event_type` a LATER pack uses to declare a tiered rule keyed to those
 * inputs (AC3's forward-compatibility half).
 *
 * The rule's conditions live in `tier_rule_conditions` on the declaring record
 * — a field this sprint adds to the pack-installer's deadline whitelist, so a
 * pack that declares one installs intact and the platform evaluates it with **no
 * schema change to `TransferAssessment`**. Proven by a fixture pack in
 * `transfer-tier-rules.test.ts`, not asserted: a test that only checks the five
 * fields exist does not discharge AC3.
 *
 * Evaluating a rule produces a STATEMENT, never an effect. No control fires on
 * these inputs in this sprint, and `TransferTierRuleOutcome.control_active` is
 * `false` for every outcome the evaluator returns.
 */
export const TRANSFER_TIER_RULE_EVENT_TYPE = 'transfer_tier_rule';
export const PRE_COLLECTION_ASSESSMENT_STATUSES = [
    'draft',
    'approved',
    'deprecated',
];
/**
 * Allowed transitions. `approved` needs a completion date (an assessment that
 * has not been made cannot be approved); `deprecated` is terminal and read-only.
 *
 * Content is editable in `draft` only (the DPIA rule, `dpia-service.ts`): once
 * a record is `approved`, the one change it accepts is the move to
 * `deprecated`. A signed-off assessment whose completion date could still be
 * moved would be an approval of a record that no longer exists.
 */
export const PRE_COLLECTION_ASSESSMENT_STATUS_TRANSITIONS = {
    draft: ['approved', 'deprecated'],
    approved: ['deprecated'],
    deprecated: [],
};
export const PRE_COLLECTION_ASSESSMENTS_COLLECTION = 'grc_pre_collection_assessments';
export const PRE_COLLECTION_ASSESSMENT_ERROR_CODES = {
    NOT_FOUND: 'PRE_COLLECTION_ASSESSMENT_NOT_FOUND',
    /** AC7 — the assessment was completed after the data was first collected. */
    ASSESSMENT_AFTER_FIRST_COLLECTION: 'ASSESSMENT_AFTER_FIRST_COLLECTION',
    REGISTRY_ITEM_NOT_FOUND: 'PRE_COLLECTION_REGISTRY_ITEM_NOT_FOUND',
    INVALID_DATE: 'PRE_COLLECTION_ASSESSMENT_INVALID_DATE',
    INVALID_TRANSITION: 'PRE_COLLECTION_ASSESSMENT_INVALID_TRANSITION',
    /** Approval needs a completion date and a written assessment. */
    NOT_COMPLETED: 'PRE_COLLECTION_ASSESSMENT_NOT_COMPLETED',
    /** A deprecated assessment is read-only. */
    DEPRECATED: 'PRE_COLLECTION_ASSESSMENT_DEPRECATED',
    /**
     * An approved assessment's content cannot be edited; it can only be
     * deprecated. A PATCH carrying any content key on an approved record is
     * refused with this code, whatever the values.
     */
    NOT_EDITABLE: 'PRE_COLLECTION_ASSESSMENT_NOT_EDITABLE',
    /**
     * The stored record is not the version the caller read: its `updated_at` no
     * longer matches `expected_updated_at`, or it changed between the server's
     * read and its write. Refused rather than written, so no check (the date
     * order, the approval's completeness) is ever passed against a record that
     * has since been replaced.
     */
    CONFLICT: 'PRE_COLLECTION_ASSESSMENT_CONFLICT',
};
/**
 * Sprint 63.37.0 (owner decision 2026-10-19) — the statuses in which a first
 * filing counts as the organisation's filed dossier: it reached the authority
 * and the authority did not reject it. A `rejected` filing does not count (the
 * dossier was not taken, so a new first filing is the remedy), nor does one
 * `closed` after a rejection; the service reads `outcome` for that case.
 */
export const DOSSIER_FILING_FILED_STATUSES = [
    'submitted',
    'supplement_requested',
    'supplement_submitted',
    'resubmitted',
    // Sprint 62.57.0 (AC-B2, D21): a recorded result counts as filed whatever
    // its effect, `failed` included — the dossier reached the authority and was
    // appraised. First-filing guidance carries the effect and whether a
    // follow-up is recorded beside it; it never advises a new first filing.
    'result_recorded',
    'result_supplement_required',
    'result_supplement_submitted',
    'result_completion_submitted',
    'accepted',
    'closed',
];
/** Sprint 25.3.5.1: Valid section status transitions. */
export const SECTION_STATUS_TRANSITIONS = {
    assigned: ['in_progress'],
    in_progress: ['completed'],
    completed: ['reviewed', 'in_progress'],
    reviewed: [],
};
/**
 * The `event_type` the applicant's cure clock is selected by - platform selector
 * vocabulary in the 64.153.0 tradition (`rights_request_*`), authored on the
 * deadline record by the pack that declares it. Nothing else about the
 * instrument (framework, authority, sla_type, period) is named in `src/`.
 *
 * Fix sprint #456 gate E12.5 pass 1: the dossier's supplement response used to
 * start a literal 15-working-day timer and its resubmission a literal
 * 10-working-day one; `docs/compliance/legal-timelines.md` section 10.4 rules
 * both out. The only installed clock for the organisation's side is the one the
 * pack declares under this event type, and it runs from an event the
 * organisation must evidence (its receipt of the authority's incomplete/invalid
 * result), not from the day a supplement request is typed in.
 */
export const DOSSIER_APPLICANT_CURE_EVENT_TYPE = 'impact_dossier_applicant_cure';
/** Stored timer-type spellings read as the current vocabulary (AC-B5). */
export const LEGACY_TIMER_TYPE_SPELLINGS = {
    mps_review: 'authority_review',
};
// ── Authority communications on a filing (Sprint 62.57.0, Slice R) ──────
/**
 * Where the communications live: their own collection, never an array on the
 * filing (work file D2), so recording one leaves the filing document unwritten.
 */
export const DOSSIER_AUTHORITY_COMMUNICATIONS_COLLECTION = 'grc_dossier_authority_communications';
/**
 * What the authority sent, as a property of correspondence rather than of any
 * instrument (AC-R1). A closed set: the platform names no procedure here, and
 * nothing branches on the kind except its label and the list filter (AC-R2).
 */
export const DOSSIER_AUTHORITY_COMMUNICATION_KINDS = [
    'receipt_acknowledgement',
    'transfer_notice',
    'result_notice',
    'supplement_request',
    'refusal',
    'other_written_communication',
];
/**
 * The filing statuses from which a communication may be recorded: `submitted`
 * onward (AC-R1). `draft`, `internal_review`, `legal_signoff` and `approved`
 * answer 409 `DOSSIER_NOT_SUBMITTED` — nothing has gone to an authority yet.
 */
export const DOSSIER_COMMUNICATION_ALLOWED_STATUSES = [
    'submitted',
    'supplement_requested',
    'supplement_submitted',
    'resubmitted',
    'result_recorded',
    'result_supplement_required',
    'result_supplement_submitted',
    'result_completion_submitted',
    'accepted',
    'rejected',
    'closed',
];
// ── Handling authority on a filing (Sprint 62.57.0, Slice A) ────────────
/**
 * Why a person states which body handles the dossier (AC-A2). A closed,
 * instrument-neutral set: each cites a recorded communication.
 */
export const DOSSIER_HANDLING_STATEMENT_BASES = [
    'transfer_notice',
    'authority_result',
    'other_written_communication',
];
// ── Sprint 62.57.0 Slice C — the completion period (D25–D33) ─────────────────
/**
 * What a completion period is opened by (D26): a person-recorded completion
 * request, or a result whose effect the governing row lists. Instrument-neutral;
 * code branches on this and on the effect only.
 */
export const DOSSIER_COMPLETION_TRIGGER_KINDS = ['completion_request', 'result'];
//# sourceMappingURL=assessment.js.map